'use client';

import type { ColumnDef, PaginationState } from '@tanstack/react-table';
import { DataTable } from '@zealot/ui/components/data-table';
import { DataTableColumnHeader } from '@zealot/ui/components/data-table-column-header';
import { useMemo } from 'react';

type SimpleDataTableColumn<TData> = {
  accessorKey: keyof TData & string;
  header: string;
  cell?: (value: unknown, row: TData) => React.ReactNode;
  enableSorting?: boolean;
  size?: number;
};

// gloss
type SimpleDataTableServerPagination = {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
};

type SimpleDataTableProps<TData> = {
  data: TData[];
  columns: SimpleDataTableColumn<TData>[];
  enableSorting?: boolean;
  enablePagination?: boolean;
  enableRowSelection?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  emptyMessage?: string;
  errorMessage?: string;
  onRowClick?: (row: TData) => void;
  pageSize?: number;
  serverPagination?: SimpleDataTableServerPagination;
  className?: string;
};

const SimpleDataTable = <TData,>({
  data,
  columns: simpleColumns,
  enableSorting = false,
  enablePagination = false,
  enableRowSelection = false,
  isLoading,
  isError,
  emptyMessage,
  errorMessage,
  onRowClick,
  pageSize = 10,
  serverPagination,
  className,
}: SimpleDataTableProps<TData>) => {
  const tanstackColumns: ColumnDef<TData, unknown>[] = useMemo(
    () =>
      simpleColumns.map((col) => ({
        accessorKey: col.accessorKey,
        header:
          (col.enableSorting ?? enableSorting)
            ? ({ column }) => <DataTableColumnHeader column={column} title={col.header} />
            : col.header,
        ...(col.cell && {
          cell: ({ getValue, row }) => col.cell!(getValue(), row.original),
        }),
        enableSorting: col.enableSorting ?? enableSorting,
        size: col.size,
      })),
    [simpleColumns, enableSorting],
  );

  const serverPaginationState: PaginationState | undefined = serverPagination && {
    pageIndex: serverPagination.page - 1,
    pageSize: serverPagination.pageSize,
  };

  const handleServerPaginationChange = (updater: PaginationState | ((old: PaginationState) => PaginationState)) => {
    if (!serverPagination || !serverPaginationState) return;
    const next = typeof updater === 'function' ? updater(serverPaginationState) : updater;
    if (next.pageSize !== serverPagination.pageSize) serverPagination.onPageSizeChange(next.pageSize);
    else if (next.pageIndex !== serverPaginationState.pageIndex) serverPagination.onPageChange(next.pageIndex + 1);
  };

  const serverPaginationProps = serverPagination && {
    manualPagination: true,
    pagination: serverPaginationState,
    onPaginationChange: handleServerPaginationChange,
    pageCount: Math.max(1, Math.ceil(serverPagination.total / serverPagination.pageSize)),
    rowCount: serverPagination.total,
  };

  return (
    <DataTable
      data={data}
      columns={tanstackColumns}
      enableSorting={enableSorting}
      enablePagination={enablePagination || !!serverPagination}
      enableRowSelection={enableRowSelection}
      showPagination={enablePagination || !!serverPagination}
      isLoading={isLoading}
      isError={isError}
      emptyMessage={emptyMessage}
      errorMessage={errorMessage}
      onRowClick={onRowClick ? (row) => onRowClick(row.original) : undefined}
      initialPagination={{ pageIndex: 0, pageSize }}
      {...serverPaginationProps}
      className={className}
    />
  );
};

export type { SimpleDataTableColumn, SimpleDataTableProps, SimpleDataTableServerPagination };
export { SimpleDataTable };
